// Pure view model for the Salary reviews overview (Story 2.1). Takes the
// GET /v1/salary-reviews/lists payload as the API sends it and returns what the
// page renders, so grouping and formatting are tested without a DOM.
//
// Date-only values ('YYYY-MM-DD') are read by their parts, never through
// new Date('YYYY-MM-DD'), which is UTC midnight and can land on the previous
// local day.

import { format } from 'date-fns'

export const EMPTY = '—'

// Review statuses in lifecycle order, with their display labels. Reviews
// arrive in Story 2.2; until then every count is 0.
export const REVIEW_STATUSES = [
  { key: 'Draft', label: 'Draft' },
  { key: 'AwaitingApproval', label: 'Awaiting approval' },
  { key: 'UnderNegotiation', label: 'Negotiating' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Applied', label: 'Applied' },
  { key: 'Declined', label: 'Declined' }
]

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/

/** 'YYYY-MM-DD' -> a local Date, or null. */
export const parseDateOnly = (value) => {
  const match = DATE_ONLY.exec(String(value || ''))
  if (!match) return null
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** '2026-12-15' -> '15 Dec 2026'. */
export const formatDateOnly = (value) => {
  const d = parseDateOnly(value)
  return d ? format(d, 'd MMM yyyy') : EMPTY
}

/** '2027-01-01' -> 'January 2027'. */
export const formatMonth = (value) => {
  const d = parseDateOnly(value)
  return d ? format(d, 'MMMM yyyy') : EMPTY
}

/** A timestamp, shown in the viewer's local day. */
export const formatTimestamp = (value) => {
  if (!value) return EMPTY
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? EMPTY : format(d, 'd MMM yyyy')
}

const QUARTER = /^(\d{4})-Q([1-4])$/
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * '2027-Q1' -> 'Q1 2027'. Story 2.8: a list IS a quarter; each review has its
 * own effective month (Story 2.9: any month from the current one on; the
 * quarter only groups and labels the list).
 */
export const quarterLabel = (quarter) => {
  const match = QUARTER.exec(String(quarter || ''))
  return match ? `Q${match[2]} ${match[1]}` : 'No quarter'
}

/** '2026-Q3' -> ['2026-07', '2026-08', '2026-09'] (month input values); [] if invalid. */
export const quarterMonths = (quarter) => {
  const match = QUARTER.exec(String(quarter || ''))
  if (!match) return []
  const first = ((Number(match[2]) - 1) * 3) + 1
  return [0, 1, 2].map((i) => `${match[1]}-${String(first + i).padStart(2, '0')}`)
}

/** '2026-07' or '2026-07-01' -> 'Jul 2026'. */
export const formatMonthShort = (value) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value || ''))
  const month = match ? Number(match[2]) : 0
  return month >= 1 && month <= 12 ? `${MONTH_NAMES[month - 1]} ${match[1]}` : EMPTY
}

/** '2026-Q3' -> 'Q3 2026 (Jul–Sep)', for the create form's quarter select. */
export const quarterOptionLabel = (quarter) => {
  const months = quarterMonths(quarter)
  if (!months.length) return quarterLabel(quarter)
  const short = (m) => MONTH_NAMES[Number(m.slice(5, 7)) - 1]
  return `${quarterLabel(quarter)} (${short(months[0])}–${short(months[2])})`
}

/**
 * The list's effective months with their people: 'Jul 2026 · 3, Aug 2026 · 2'
 * from the API's monthCounts; '' when the list has no people.
 */
export const monthBreakdown = (monthCounts) => {
  const rows = Array.isArray(monthCounts) ? monthCounts : []
  return rows
    .filter((m) => m && Number(m.count) > 0)
    .map((m) => `${formatMonthShort(m.month)} · ${Number(m.count)}`)
    .join(', ')
}

export const peopleLabel = (count) => {
  const n = Number(count) || 0
  return n === 1 ? '1 person' : `${n} people`
}

const createdMs = (list) => {
  const t = new Date(list.createdAt).getTime()
  return Number.isFinite(t) ? t : 0
}

/** Quarter desc, then createdAt desc — the order the API sends. */
export const compareLists = (a, b) => {
  const qa = a.quarter || ''
  const qb = b.quarter || ''
  if (qa !== qb) return qa < qb ? 1 : -1
  const byCreated = createdMs(b) - createdMs(a)
  if (byCreated !== 0) return byCreated
  return (b.id || 0) - (a.id || 0)
}

export const sortLists = (lists) => [...(lists || [])].sort(compareLists)

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** '$5,880' (whole dollars; cents below $100 so hourly rates stay exact). */
export const formatUsd = (value) => {
  const n = toNumber(value)
  if (n === null) return EMPTY
  const digits = Math.abs(n) < 100 ? 2 : 0
  const text = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  })
  return `${n < 0 ? '−' : ''}$${text}`
}

/** A monthly USD figure: '$1,100/mo'. */
export const formatUsdPerMonth = (value) => {
  const n = toNumber(value)
  return n === null ? EMPTY : `${formatUsd(n)}/mo`
}

/**
 * The label a total carries when it leaves out people whose salary is hidden
 * from the viewer: "partial: N hidden", or null when nothing is hidden. With
 * `unavailableCount` (rows whose margin data could not be loaded) it names
 * those too: "partial: 1 hidden, 2 unavailable".
 */
export const partialLabel = (hiddenCount, unavailableCount = 0) => {
  const count = (value) => {
    const n = toNumber(value)
    return n !== null && n > 0 ? n : 0
  }
  const parts = []
  if (count(hiddenCount)) parts.push(`${count(hiddenCount)} hidden`)
  if (count(unavailableCount)) parts.push(`${count(unavailableCount)} unavailable`)
  return parts.length ? `partial: ${parts.join(', ')}` : null
}

/**
 * The status-segment bar: one segment per status that has reviews, in
 * lifecycle order; each grows by its count (flex-grow), so gaps never clip.
 */
export const buildSegments = (statusCounts) => {
  const counts = statusCounts || {}
  const total = REVIEW_STATUSES.reduce((sum, s) => sum + (Number(counts[s.key]) || 0), 0)
  if (!total) return []
  return REVIEW_STATUSES.filter((s) => Number(counts[s.key]) > 0).map((s) => ({
    key: s.key,
    label: s.label,
    count: Number(counts[s.key])
  }))
}

/** One card's view. */
export const buildListCard = (list) => {
  const counts = list.statusCounts || {}
  return {
    id: list.id,
    name: list.name,
    createdBy: list.createdByName || EMPTY,
    createdOn: formatTimestamp(list.createdAt),
    // Story 2.8: the list's quarter and how its people spread over its months.
    quarter: quarterLabel(list.quarter),
    months: monthBreakdown(list.monthCounts),
    decideBy: list.decideBy ? formatDateOnly(list.decideBy) : EMPTY,
    people: peopleLabel(list.peopleCount),
    statuses: REVIEW_STATUSES.map((s) => ({ ...s, count: Number(counts[s.key]) || 0 })),
    segments: buildSegments(counts),
    // Story 2.7: Σ (agreed × overhead − current × overhead; 1 for hourly) over the non-Declined
    // reviews, in USD, from the API (summary.js); null ("—") when not
    // computed (another quarter) or unknown.
    monthlyCost: formatUsdPerMonth(list.monthlyCostIncrease),
    partial: partialLabel(list.monthlyCostHiddenCount, list.monthlyCostUnavailableCount),
    closed: list.closed === true
  }
}

/** A signed monthly increase: '+$2,200', '−$50', '$0'; '—' when unknown or hidden. */
export const formatIncrease = (value) => {
  const n = toNumber(value)
  if (n === null) return EMPTY
  return n > 0 ? `+${formatUsd(n)}` : formatUsd(n)
}

// The status words of the row's breakdown line ("2 awaiting approval").
const STATUS_WORDS = {
  Draft: 'draft',
  AwaitingApproval: 'awaiting approval',
  UnderNegotiation: 'negotiating',
  Approved: 'approved',
  Applied: 'applied',
  Declined: 'declined'
}

/** '1 draft · 2 awaiting approval' in lifecycle order, zero counts left out; '' with none. */
export const statusBreakdown = (statusCounts) => buildSegments(statusCounts)
  .map((s) => `${s.count} ${STATUS_WORDS[s.key]}`)
  .join(' · ')

/** '29 Sep' this year, '29 Sep 2025' in another. */
const shortDate = (d, now) => format(d, d.getFullYear() === now.getFullYear() ? 'd MMM' : 'd MMM yyyy')

/**
 * The row's meta line: 'Opened 29 Sep by Alla Petrenko · decide by 31 Oct'.
 * A closed list leaves out the decide-by date (the API sends no closing
 * date, so the row says "Closed" with a pill instead).
 */
export const listMeta = (list, now = new Date()) => {
  const created = list && list.createdAt ? new Date(list.createdAt) : null
  let opened = created && !Number.isNaN(created.getTime()) ? `Opened ${shortDate(created, now)}` : 'Opened'
  if (list && list.createdByName) opened += ` by ${list.createdByName}`
  const decide = list && list.closed !== true ? parseDateOnly(list.decideBy) : null
  return decide ? `${opened} · decide by ${shortDate(decide, now)}` : opened
}

/**
 * The list page's meta line under its title: 'Opened 29 Sep by Alla Petrenko
 * · Quarter Q4 2026 · decide by 31 Oct' (each part only when known).
 */
export const detailMeta = (list, now = new Date()) => {
  if (!list) return ''
  const created = list.createdAt ? new Date(list.createdAt) : null
  let opened = created && !Number.isNaN(created.getTime()) ? `Opened ${shortDate(created, now)}` : null
  if (opened && list.createdByName) opened += ` by ${list.createdByName}`
  const decide = parseDateOnly(list.decideBy)
  return [opened, `Quarter ${quarterLabel(list.quarter)}`, decide ? `decide by ${shortDate(decide, now)}` : null].filter(Boolean).join(' · ')
}

/** One overview row: the card's view plus the design's meta, breakdown and signed increase. */
export const buildListRow = (list, now = new Date()) => ({
  ...buildListCard(list),
  meta: listMeta(list, now),
  peopleCount: Number(list.peopleCount) || 0,
  breakdown: statusBreakdown(list.statusCounts),
  increase: formatIncrease(list.monthlyCostIncrease)
})

/** 'YYYY-Qn' of a local date. */
export const quarterOfDate = (now = new Date()) => `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`

/**
 * The quarters the selector offers: every quarter that has a list, plus the
 * selected one (the API's current quarter may have none), newest first.
 */
export const quarterOptions = (lists, selected = null) => {
  const quarters = [...new Set([...(lists || []).map((l) => l.quarter), selected].filter(Boolean))]
  return quarters.sort((a, b) => (a < b ? 1 : -1)).map((q) => ({ value: q, label: quarterLabel(q) }))
}

/** A quarter before the current one (its figures use today's rates). */
export const isPastQuarter = (quarter, now = new Date()) => Boolean(quarter) && quarter < quarterOfDate(now)

export const PAST_QUARTER_NOTE = "Past quarters are recomputed at today's rates"

/** Lists of `quarter` that are still open (not closed). */
export const openListCount = (lists, quarter) => (lists || []).filter((l) => l && l.quarter === quarter && l.closed !== true).length

/**
 * The 4 overview tiles for the selected quarter (summary.quarterSummary):
 * open reviews (across the quarter's open lists), awaiting approval with the
 * oldest wait, the Below count and the approved monthly increase (Approved +
 * Applied). Each total carries its own partial label; money the API could
 * not compute, or the viewer may not see, shows "—".
 */
export const buildQuarterTiles = (quarters, quarter, lists = []) => {
  const q = (quarters || []).find((x) => x.quarter === quarter) || {}
  const awaiting = Number(q.awaitingCount) || 0
  const days = q.oldestWaitingDays === null || q.oldestWaitingDays === undefined ? null : Number(q.oldestWaitingDays)
  let waiting = 'none waiting'
  if (awaiting > 0) waiting = days === null ? EMPTY : `oldest waiting ${days === 1 ? '1 day' : `${days} days`}`
  const hasSummary = Object.keys(q).length > 0
  const openLists = openListCount(lists, quarter)
  return [
    {
      key: 'open',
      label: 'Open reviews',
      value: String(Number(q.openCount) || 0),
      sub: `across ${openLists} open ${openLists === 1 ? 'list' : 'lists'}`,
      partial: null
    },
    { key: 'awaiting', label: 'Awaiting approval', value: String(awaiting), sub: waiting, partial: null },
    {
      key: 'below',
      label: 'Below target margin',
      value: String(Number(q.belowCount) || 0),
      sub: 'proposed raise breaks level target',
      partial: partialLabel(q.belowHiddenCount, q.belowUnavailableCount)
    },
    {
      key: 'approved',
      label: 'Approved increase',
      value: hasSummary ? formatIncrease(q.approvedMonthlyCostIncrease) : EMPTY,
      sub: 'per month, written to Salaries',
      partial: partialLabel(q.approvedHiddenCount, q.approvedUnavailableCount)
    }
  ]
}

/**
 * Lists grouped under quarter headings, newest quarter first; inside a
 * quarter, newest list first.
 * @returns {{quarter: string, label: string, lists: Object[]}[]}
 */
export const groupByQuarter = (lists) => {
  const groups = []
  sortLists(lists).forEach((list) => {
    const last = groups[groups.length - 1]
    if (last && last.quarter === list.quarter) {
      last.lists.push(buildListCard(list))
    } else {
      groups.push({ quarter: list.quarter, label: quarterLabel(list.quarter), lists: [buildListCard(list)] })
    }
  })
  return groups
}

/** 'YYYY-MM' of `now` (local), plus `offset` months. */
export const monthValue = (now = new Date(), offset = 0) => {
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// The API decides "today" in Europe/Kyiv (production TZ): so does the UI,
// whatever the viewer's own time zone.
const KYIV_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit' })

/** Today as 'YYYY-MM-DD' on the Europe/Kyiv calendar. */
export const todayValue = (now = new Date()) => KYIV_DAY.format(now)

/** The current Europe/Kyiv month, 'YYYY-MM'. */
export const currentMonthValue = (now = new Date()) => todayValue(now).slice(0, 7)

/** The current Europe/Kyiv quarter, 'YYYY-Qn'. */
export const currentQuarterValue = (now = new Date()) => {
  const month = currentMonthValue(now)
  return `${month.slice(0, 4)}-Q${Math.ceil(Number(month.slice(5, 7)) / 3)}`
}

/** 'YYYY-Qn' shifted by `offset` quarters. */
export const addQuarters = (quarter, offset) => {
  const match = QUARTER.exec(String(quarter || ''))
  if (!match) return null
  const index = (Number(match[1]) * 4) + (Number(match[2]) - 1) + offset
  return `${Math.floor(index / 4)}-Q${(index % 4) + 1}`
}

/** The create form's quarters: the current one and the next 4 (Europe/Kyiv). */
export const createQuarterOptions = (now = new Date()) => {
  const current = currentQuarterValue(now)
  return [0, 1, 2, 3, 4].map((i) => {
    const value = addQuarters(current, i)
    return { value, label: quarterOptionLabel(value) }
  })
}

/** 'YYYY-MM' plus `offset` months. */
export const shiftMonth = (month, offset) => {
  const index = (Number(month.slice(0, 4)) * 12) + (Number(month.slice(5, 7)) - 1) + offset
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
}

/**
 * Story 2.9: the month a new review defaults to, the next Europe/Kyiv month
 * ('YYYY-MM'), as the API defaults it.
 */
export const nextMonthValue = (now = new Date()) => shiftMonth(currentMonthValue(now), 1)

// Story 2.9: the month select offers the current month and the next 12.
export const MONTHS_AHEAD = 12

export const CURRENT_OR_LATER_MESSAGE = 'Pick the current month or later'

/**
 * Story 2.9: whether `month` ('YYYY-MM') may be a review's effective month,
 * as the API judges it: the current Europe/Kyiv month or any later one. The
 * list's quarter no longer restricts it.
 */
export const isAllowedReviewMonth = (month, now = new Date()) => /^\d{4}-\d{2}$/.test(String(month || '')) && month >= currentMonthValue(now)

/**
 * Story 2.9: the effective-month select: the current Europe/Kyiv month and
 * the next 12. `keep` (a saved month) stays selectable and is listed even
 * when it lies outside those months (e.g. a month that has passed).
 * @returns {{value: string, label: string, disabled: boolean}[]}
 */
export const monthOptions = (now = new Date(), keep = null) => {
  const current = currentMonthValue(now)
  const months = Array.from({ length: MONTHS_AHEAD + 1 }, (_, i) => shiftMonth(current, i))
  const values = keep && !months.includes(keep) ? [...months, keep].sort() : months
  return values.map((value) => ({
    value,
    label: formatMonthShort(value),
    disabled: value !== keep && value < current
  }))
}

/**
 * Client-side checks for the "New review list" form, mirroring the API. The
 * API remains the judge (its 400/409 are shown inline too).
 * @returns {Object<string,string>} field -> message; empty when valid
 */
export const validateNewList = ({ name, quarter, decideBy }, now = new Date()) => {
  const errors = {}
  if (!String(name || '').trim()) errors.name = 'Enter a name.'
  else if (String(name).trim().length > 150) errors.name = 'Use 150 characters or fewer.'
  if (!QUARTER.test(String(quarter || ''))) errors.quarter = 'Choose a quarter.'
  else if (quarter < currentQuarterValue(now)) errors.quarter = 'The quarter cannot be before the current quarter.'
  if (decideBy && !parseDateOnly(decideBy)) errors.decideBy = 'Enter a valid date.'
  else if (decideBy && decideBy < todayValue(now)) errors.decideBy = 'The decide-by date cannot be before today.'
  return errors
}

/** The POST body. */
export const toCreateBody = ({ name, quarter, decideBy }) => ({
  name: String(name || '').trim(),
  quarter,
  decideBy: decideBy || null
})

/**
 * Where a failed create's message belongs: a 409 is about the name; anything
 * else is shown for the whole form.
 */
export const placeCreateError = (error) => {
  if (!error) return {}
  if (error.status === 409) return { name: error.message }
  return { form: error.message }
}
